import { useState, useEffect, memo, useRef } from 'react';
import { GatewayLogMetadataType, GatewayLogType, Site } from '../../utils/types';
import { addLineBreaks, siteName } from '../../utils/functions';
import '../../App.css';
import './logs.css';

import { ChevronDown, ChevronUp, Circle, CircleCheckBig, Copy } from 'lucide-react';

import StatusIndicator from '../../tools/status-indicator';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import EditableSelect from '../../tools/editable-select';
import Scrollbox from '../../tools/scrollbox';
import MiniButton from '../../tools/mini-button';
import CollapsingPanel, {
  PanelPosition,
} from '../../tools/collapsing-panel/collapsing-panel';
import {
  customStylesDark,
  customStylesLight,
} from '../../utils/custom-styles-select-box';
import { rG_setSite } from '../../app/global-slice';
import { useTranslation } from 'react-i18next';
import { logFilterIndex } from './filters';
import isEqual from 'lodash/isEqual';
import { handleEvent } from '../../app-actions';
import {
  Event,
  EventType,
  EventTarget,
  GatewayLogsEventParams,
} from '../../utils/events';
import { toast } from 'react-toastify';
import {
  GatewayLogFilterParamsType,
  removeGatewayLogFilter,
  resetGatewayLogFilters,
  setGatewayLogFilter,
  setSelectedSite,
  setSelectedTimestampRange,
  setSiteOptions,
  siteOptionType,
  timestampOptionType,
} from './logs-slice';

//  ------------------------------------------------------------
//
//  Get the string name of a log level from its number.
//
//  ------------------------------------------------------------
const getLevelName = (level: number) => {
  switch (level) {
    case 50:
      return 'CRITICAL';
    case 40:
      return 'ERROR';
    case 30:
      return 'WARNING';
    case 20:
      return 'INFO';
    case 10:
      return 'DEBUG';
    case 0:
    default:
      return 'NOTSET';
  }
};

//  ------------------------------------------------------------
//
//  Component for Level cell in log table row.
//
//  ------------------------------------------------------------
const LogLevelCell = (props: { level: number }) => {
  return (
    <td className="log-table-level">
      <StatusIndicator status={getLevelName(props.level).toUpperCase()} />
    </td>
  );
};

//  ------------------------------------------------------------
//
//  Component for Date cell in log table row.
//
//  ------------------------------------------------------------
const LogDateCell = (props: { created_at: string; metadata: GatewayLogMetadataType }) => {
  const timestampParts = (() => {
    // Handle unparsable timestamp strings.
    try {
      return new Date(props.created_at).toLocaleString().split(', ');
    } catch (e) {
      console.log('Failed to parse log timestamp: ' + e);
      return [];
    }
  })();

  return (
    <td className="log-table-date">
      <div className="log-table-body-cell">
        {timestampParts.length === 2 ? (
          <>
            {timestampParts[0]}
            <br />
            {timestampParts[1]}
          </>
        ) : (
          '-'
        )}
        {props.metadata.repeat_count && props.metadata.repeat_count > 0 ? (
          <>
            <br />
            <button
              id="interactive-sessions-site"
              className="repeat-button text-colour"
              onClick={() => {
                toast.dismiss();
                if (!props.metadata.repeat_dates) return;
                toast.info(
                  <div>
                    <ul>
                      {props.metadata.repeat_dates.map((item, index) => (
                        <li key={index}>{new Date(item).toLocaleString()}</li>
                      ))}
                    </ul>
                  </div>,
                  {
                    className: 'log-repeat-date-toast',
                    position: 'top-left',
                    hideProgressBar: false,
                    closeOnClick: false,
                    draggable: false,
                    progress: undefined,
                    icon: false,
                    style: { overflow: 'auto' },
                  },
                );
              }}
            >
              {(props.metadata.repeat_count > 999
                ? '999+'
                : props.metadata.repeat_count) + ' repeats'}
            </button>
          </>
        ) : (
          ''
        )}
      </div>
    </td>
  );
};

//  ------------------------------------------------------------
//
//  Component for log Message cell in log table row.
//
//  ------------------------------------------------------------
const LogMessageCell = (props: { message: string }) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;

    // Use a resize observer to determine if a log message cell contents are overflowing
    // and "show more/less" buttons should be used.
    const observer = new ResizeObserver(() => {
      if (container && !expanded)
        setIsOverflowing(
          container.scrollHeight > container.clientHeight ||
            container.scrollWidth > container.clientWidth,
        );
    });

    if (container) observer.observe(container);

    return () => observer.disconnect();
  }, [expanded]);

  return (
    <td className="log-table-message fr">
      <MiniButton
        className="text-colour"
        onClick={() => {
          navigator.clipboard.writeText(props.message);
        }}
        style={{
          margin: '3px 10px 0px 0px',
        }}
      >
        <Copy width={30} height={30} strokeWidth={1.5} />
      </MiniButton>
      <div
        ref={containerRef}
        className="fr"
        style={{
          ...(isOverflowing
            ? expanded
              ? {}
              : { overflowX: 'hidden', whiteSpace: 'nowrap' }
            : { whiteSpace: 'nowrap', overflowX: 'hidden' }),
          ...{ alignItems: 'start', margin: '8px 0px 0px 0px' },
        }}
      >
        {addLineBreaks(props.message)}
      </div>
      <div style={{ flex: '1 1' }} />
      {isOverflowing && (
        <button
          className="text-colour log-message-expand-button fr"
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded === true && <ChevronUp width={30} height={30} strokeWidth={1.5} />}
          {expanded === false && <ChevronDown width={30} height={30} strokeWidth={1.5} />}
        </button>
      )}
    </td>
  );
};

//  ------------------------------------------------------------
//
//  Component for log entry row in log table.
//
//  ------------------------------------------------------------
const LogTableRows = (props: {
  entries: GatewayLogType[];
  filters: GatewayLogFilterParamsType[];
  sites: Site[];
  showSite: boolean;
}) => {
  if (props.entries.length <= 0) return null;

  // Map a list of log entries to build table rows.
  return (
    <>
      {props.entries
        .filter((entry) =>
          Array.from(props.filters.values()).every((filterParam) => {
            let filterFunction = logFilterIndex.get(filterParam.filter);
            if (!filterFunction) return false;
            let args = filterParam.args ? filterParam.args : {};
            return filterFunction(entry, args);
          }),
        )
        .map((entry: GatewayLogType, index: number) => {
          const reversedIndex = props.entries.length - 1 - index;
          const rowID = `log_table_row_${reversedIndex}`;
          return (
            <tr id={rowID} key={reversedIndex} tabIndex={0} className="log-table-row">
              <LogDateCell created_at={entry.created_at} metadata={entry.metadata} />
              <td className="log-table-component">
                <div className="log-table-body-cell">{entry.component}</div>
              </td>
              <LogLevelCell level={entry.level} />
              <td
                className="log-table-site"
                style={{ display: props.showSite ? 'table-cell' : 'none' }}
              >
                <div className="log-table-body-cell">
                  {props.sites.find((site) => site.site_uuid === entry.metadata.site_uuid)
                    ?.site || 'N/A'}
                </div>
              </td>
              <LogMessageCell message={entry.message} />
            </tr>
          );
        })}
    </>
  );
};

//  ------------------------------------------------------------
//
//  Component for log table.
//
//  ------------------------------------------------------------
export const Logs = memo(
  (props: {
    entries: GatewayLogType[];
    focusRow: React.MutableRefObject<string | undefined>;
  }) => {
    // Component is wrapped in a memo() so it only re-renders if its props change.
    const logTabRef = useRef<HTMLDivElement | null>(null);
    const isVisibleRef = useRef<boolean>(false);
    const sGatewayLogFilters = useAppSelector((state) => state.logs.gatewayLogFilters);
    const sG_darkMode: boolean = useAppSelector((state) => state.global.darkMode);
    const sD_sites: Site[] = useAppSelector((state) => state.data.sites);
    const selectedSiteOption = useAppSelector((state) => state.logs.selectedSite);
    const selectedTimestampRangeOption = useAppSelector(
      (state) => state.logs.selectedTimestampRange,
    );
    const siteOptions = useAppSelector((state) => state.logs.siteOptions);
    const timestampRangeOptions = useAppSelector(
      (state) => state.logs.TimestampRangeOptions,
    );
    const sNarrowScreen: boolean = useAppSelector((state) => state.global.narrowScreen);

    const dispatch = useAppDispatch();

    const { i18n, t } = useTranslation();

    useEffect(() => {
      let _siteOptions: siteOptionType[] = [
        { value: 'All', label: 'All', icon: undefined },
      ];
      _siteOptions = _siteOptions.concat(
        sD_sites
          .filter((site) => site.admin === true)
          .map((site) => ({
            value: site.site_uuid ?? '',
            label: siteName({ site: site }),
            icon: site.icon,
          })),
      );
      dispatch(setSiteOptions(_siteOptions));
    }, [sD_sites, dispatch, setSiteOptions]);

    const gatewayLogFilterApplied = (inputFilterParam: GatewayLogFilterParamsType) => {
      return (
        sGatewayLogFilters.find(
          (filterParam) =>
            filterParam.filter === inputFilterParam.filter &&
            isEqual(filterParam.args, inputFilterParam.args),
        ) !== undefined
      );
    };

    const handleVisibilityChange = (visible: boolean) => {
      if (visible) {
        // Check if there's a row to focus and focus it when switching to Logs tab.
        if (props.focusRow.current === undefined) return;

        const rowElement = document.getElementById(props.focusRow.current);
        if (rowElement === null) return;

        rowElement.focus({ preventScroll: false });
        return;
      }

      // Reset focusRow when switching to another tab
      props.focusRow.current = undefined;
    };

    const visibilityCallback = (
      entries: IntersectionObserverEntry[],
      observer: IntersectionObserver,
    ) => {
      const currentlyVisible = entries[0].isIntersecting;

      // handle switches to and from the Logs tab.
      if (isVisibleRef.current !== currentlyVisible) {
        isVisibleRef.current = currentlyVisible;
        handleVisibilityChange(currentlyVisible);
      }
    };

    useEffect(() => {
      // Add observer to detect and set tab visibility.
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.0,
      };
      const visibilityObserver = new IntersectionObserver(visibilityCallback, options);

      if (logTabRef.current) {
        visibilityObserver.observe(logTabRef.current);
      }

      // Cleanup observer on component dismount.
      return () => visibilityObserver.disconnect();
    }, []);

    useEffect(() => {
      const params: GatewayLogsEventParams = {
        timestampRange: selectedTimestampRangeOption.value,
      };
      const newEvent: Event = {
        eventTarget: EventTarget.APP,
        eventType: EventType.LOAD_BACKEND_LOGS,
        parameters: params,
      };
      dispatch(handleEvent({ event: newEvent }));
    }, [selectedTimestampRangeOption]);

    return (
      <div
        style={{
          flex: '1 1',
          height: '100%',
          display: 'flex',
          flexDirection: 'row',
          padding: '0px 5px 0px 0px',
          position: 'relative',
        }}
      >
        <CollapsingPanel
          className="br-d-r"
          position={PanelPosition.LEFT}
          title={t('Filter')}
          height="100%"
          width="308px"
        >
          {/* the column containing filter boxes, side panels, etc. */}
          <Scrollbox
            direction="vertical"
            width="100%"
            flex="1 1"
            flexDirection="column"
            padding="0px 10px 10px 10px"
          >
            <label
              id="logs-timestamp-range-label"
              className="text-colour"
              style={{
                margin: '0px 0px 5px 0px',
              }}
            >
              Timestamp range:
            </label>
            <div
              style={{
                flex: '0 0 auto',
                minWidth: '277px',
                width: '100%',
              }}
            >
              <EditableSelect
                aria-labelledby="logs-timestamp-range-label"
                gatewayTheme={sG_darkMode ? 'dark' : 'light'}
                styles={sG_darkMode ? customStylesDark : customStylesLight}
                isDisabled={false}
                isSearchable={false}
                options={timestampRangeOptions}
                defaultValue={timestampRangeOptions[0].value}
                value={selectedTimestampRangeOption}
                onChange={(selectedOption: timestampOptionType) => {
                  dispatch(setSelectedTimestampRange(selectedOption));
                }}
                isClearable={false}
                showSiteIcon={false}
              />
            </div>
            <div style={{ flex: '0 0 20px' }} />
            <div
              style={{
                display: siteOptions.length > 1 ? 'flex' : 'none',
                flexDirection: 'column',
              }}
            >
              <div
                className="border-colour fc"
                data-faint-border="T"
                style={{
                  flex: '0 0 auto',
                  height: 'auto',
                  padding: '5px',
                  borderRadius: '6px',
                }}
              >
                <button
                  type="button"
                  title="Toggle user logs"
                  className="action-button text-colour fr"
                  onClick={(event) => {
                    if (
                      !gatewayLogFilterApplied({
                        filter: 'user',
                        args: { apply: true },
                      })
                    ) {
                      dispatch(
                        setGatewayLogFilter({
                          filter: 'siteOnly',
                          args: { apply: false },
                        }),
                      );
                      dispatch(removeGatewayLogFilter('siteUuid'));
                      dispatch(
                        setGatewayLogFilter({
                          filter: 'user',
                          args: { apply: true },
                        }),
                      );
                    }
                  }}
                  style={{
                    height: '22px',
                    alignItems: 'center',
                    background: 'transparent',
                    border: 'transparent',
                    flex: '0 0 auto',
                    marginTop: '5px',
                  }}
                >
                  {gatewayLogFilterApplied({
                    filter: 'user',
                    args: { apply: true },
                  }) === true && (
                    <CircleCheckBig width={20} height={20} strokeWidth={1.5} />
                  )}
                  {gatewayLogFilterApplied({
                    filter: 'user',
                    args: { apply: true },
                  }) !== true && <Circle width={20} height={20} strokeWidth={1.5} />}
                  <div style={{ marginLeft: '5px' }}>View user logs</div>
                </button>
                <div style={{ flex: '0 0 20px' }} />
                <button
                  id="site-only-logs-site-label"
                  type="button"
                  title="Toggle site-only logs"
                  className="action-button text-colour fr"
                  onClick={(event) => {
                    if (
                      !gatewayLogFilterApplied({
                        filter: 'siteOnly',
                        args: { apply: true },
                      })
                    ) {
                      dispatch(
                        setGatewayLogFilter({
                          filter: 'user',
                          args: { apply: false },
                        }),
                      );
                      dispatch(
                        setGatewayLogFilter({
                          filter: 'siteOnly',
                          args: { apply: true },
                        }),
                      );
                      if (selectedSiteOption.label !== 'All') {
                        dispatch(
                          setGatewayLogFilter({
                            filter: 'siteUuid',
                            args: { uuid: selectedSiteOption.value },
                          }),
                        );
                      }
                    }
                  }}
                  style={{
                    height: '22px',
                    alignItems: 'center',
                    background: 'transparent',
                    border: 'transparent',
                    flex: '0 0 auto',
                  }}
                >
                  {gatewayLogFilterApplied({
                    filter: 'siteOnly',
                    args: { apply: true },
                  }) === true && (
                    <CircleCheckBig width={20} height={20} strokeWidth={1.5} />
                  )}
                  {gatewayLogFilterApplied({
                    filter: 'siteOnly',
                    args: { apply: true },
                  }) !== true && <Circle width={20} height={20} strokeWidth={1.5} />}
                  <div style={{ marginLeft: '5px' }}>View logs for site:</div>
                </button>
                <div style={{ flex: '0 0 10px' }} />
                <div
                  style={{
                    flex: '0 0 auto',
                    minWidth: '277px',
                    width: '100%',
                    marginBottom: '5px',
                  }}
                >
                  <EditableSelect
                    aria-labelledby="site-only-logs-site-label"
                    gatewayTheme={sG_darkMode ? 'dark' : 'light'}
                    styles={sG_darkMode ? customStylesDark : customStylesLight}
                    isDisabled={gatewayLogFilterApplied({
                      filter: 'siteOnly',
                      args: { apply: false },
                    })}
                    isSearchable={false}
                    options={siteOptions}
                    defaultValue={siteOptions[0].label}
                    value={selectedSiteOption}
                    onChange={(selectedOption: siteOptionType) => {
                      if (selectedOption === null || selectedOption.value === 'All') {
                        dispatch(removeGatewayLogFilter('siteUuid'));
                      } else {
                        dispatch(
                          setGatewayLogFilter({
                            filter: 'siteUuid',
                            args: { uuid: String(selectedOption.value) },
                          }),
                        );
                        dispatch(
                          rG_setSite(
                            sD_sites.find(
                              (site) => site.site_uuid === selectedOption?.value,
                            ) ?? {},
                          ),
                        );
                      }
                      dispatch(setSelectedSite(selectedOption));
                    }}
                    isClearable={false}
                    showSiteIcon={true}
                  />
                </div>
              </div>
            </div>
            <div style={{ flex: '0 0 10px' }} />
          </Scrollbox>
        </CollapsingPanel>
        <div
          ref={logTabRef}
          style={{
            flex: '1 1',
            width: '100%',
            height: '100%',
            padding: '0px 5px 0px 0px',
          }}
        >
          <div className="panel-main" style={{ paddingTop: '0px' }}>
            <div className="log-table-body-scrollable-container">
              <table className="log-table">
                <thead className="log-table-head">
                  <tr className="log-table-row text-colour">
                    <th className="log-table-date">Timestamp</th>
                    <th className="log-table-component">Component</th>
                    <th className="log-table-level">Level</th>
                    <th
                      className="log-table-site"
                      style={{
                        display: gatewayLogFilterApplied({
                          filter: 'siteOnly',
                          args: { apply: true },
                        })
                          ? 'table-cell'
                          : 'none',
                      }}
                    >
                      Site
                    </th>
                    <th className="log-table-message">Message</th>
                  </tr>
                </thead>
                <tbody className="log-table-body text-colour">
                  <LogTableRows
                    entries={props.entries}
                    filters={sGatewayLogFilters}
                    sites={sD_sites}
                    showSite={gatewayLogFilterApplied({
                      filter: 'siteOnly',
                      args: { apply: true },
                    })}
                  />
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  },
);
